import type { DepGraphActivity } from "../../../models";
import { type RowLayoutEntry } from "../depCore/graphRows";

export type ViewMode = "zone" | "date";

/** Layout key of the transient mid-table insert row. */
export const INSERT_KEY = "g-insert";

/** A staged activity the graph doesn't carry yet (the forward pass is held
 *  while an editing session is open). Rendered as a normal row with "—" dates
 *  at the exact spot it was typed; Done recalculates and it takes its real
 *  place + dates. */
export interface LocalAddRow {
  draftId: string;
  name: string;
  code: string;
  codeName: string;
  color: string;
  zone: { a: string; b: string; c: string } | null;
  days: number;
  /** The activity it was typed beneath (null = top of its level). */
  positionAfterId: string | null;
  /** Board start/finish (ISO) once the activity has been auto-placed — so it
   *  renders with real dates immediately, before the dep-graph refetch carries
   *  it. Absent until placed (renders with "—"). */
  start?: string | null;
  finish?: string | null;
}

export function syntheticNode(a: LocalAddRow): DepGraphActivity {
  const start = a.start ?? null;
  const finish = a.finish ?? null;
  return {
    id: a.draftId,
    is_new: true,
    in_scope: true,
    name: a.name,
    task_code: "",
    status: "TK_NotStart",
    task_type: "TT_Task",
    duration_days: a.days,
    start,
    finish,
    actual_start: null,
    actual_finish: null,
    drive_start: start,
    drive_finish: finish,
    complete: false,
    work_code: a.code,
    work_name: a.codeName,
    section: "",
    zone: a.zone ?? { a: "", b: "", c: "" },
    color: a.color,
    cstr_type: null,
    cstr_date: null,
  };
}

/** Weave the not-yet-calculated adds into the layout at the spot they were
 *  typed: directly below their position anchor (which may itself be a local
 *  add placed by an earlier iteration), or at the top of their level. */
export function injectLocalAdds(
  entries: RowLayoutEntry[],
  locals: LocalAddRow[],
): RowLayoutEntry[] {
  if (locals.length === 0) return entries;
  const out = [...entries];
  for (const a of locals) {
    const zoneKey = a.zone ? `${a.zone.a}||${a.zone.b}||${a.zone.c}` : null;
    const entry: RowLayoutEntry = {
      kind: "activity",
      key: a.draftId,
      activity: syntheticNode(a),
      zoneKey,
      predDrivers: [],
      succDrivers: [],
      rowIndex: 0,
    };
    let insertIdx = -1;
    if (a.positionAfterId) {
      const ai = out.findIndex(
        (x) => x.kind === "activity" && x.activity.id === a.positionAfterId,
      );
      if (ai >= 0) {
        insertIdx = ai + 1;
      }
    }
    if (insertIdx < 0) {
      const hi = out.findIndex(
        (x) => x.kind === "header" && x.key === `h-${zoneKey}`,
      );
      const gi = out.findIndex((x) => x.kind === "ghost" && x.key === `g-${zoneKey}`);
      insertIdx = hi >= 0 ? hi + 1 : gi >= 0 ? gi : out.length;
    }
    out.splice(insertIdx, 0, entry);
  }
  return out;
}
